import { _ } from '@icc/shared/helpers';
import { SalesDocumentType } from '../sales-document-types';
import { SalesDocumentExchangeRate } from './document-exchange-rate.model';
import { DocumentPositionGroupSchema } from './position-group.model';
import { SalesDocumentClient } from './sales-document-client.model';
import { DocumentAuthor } from './document-author.model';
import { Document } from './document.model';

export interface SalesDocument extends Document {
    type: 'sales';

    /**
     * ID szansy sprzedażowej
     */
    externalChanceId?: number | null;

    /**
     * Id rynku sprzedaży
     */
    sellerMarketId?: number;

    /**
     * Id firmy sprzedaży
     */
    sellerCompanyId?: number;

    /**
     * Id oddziału sprzedaży
     */
    sellerResourceId?: number;

    /**
     * Id użytkownika sprzedaży (dealera, kontrahenta)
     */
    sellerUserId?: number;

    /**
     * Id działu produktowego firmy sprzedaży (jeśli dokument przypisany do działu)
     */
    sellerProductDepartmentId?: number;

    /**
     * Id firmy zakupu
     */
    buyerCompanyId?: number;

    /**
     * Id oddziału zakupu
     */
    buyerResourceId?: number;

    /**
     * Id użytkownika zakupu (handlowca, opiekuna)
     */
    buyerUserId?: number;

    /**
     * Id kontrahenta zakupu
     */
    buyerContractorId?: number;

    /**
     * typ dokumentu (readonly – nigdy nie zmieniamy typu dokumentu
     */
    salesDocumentTypeId?: SalesDocumentType;

    /**
     * Nazwa dokumentu
     */
    documentName?: string;

    /**
     * Kto zajmuje się dokumentem: seller - sprzedawca, buyer - kupujący, unassigned - stan nieokreślony
     */
    assignedTo: 'seller' | 'buyer' | 'unassigned';

    /**
     * Referencja klienta
     */
    clientReference?: string;

    /**
     * JSON - dane klienta wyświetlane na ofercie.
     * Wartości przepisane z danych klienta, albo wpisane ręcznie na dokumencie
     * (w starym dokumencie to był szereg pól, jak name, surname, delivery_address, …)
     */
    client?: SalesDocumentClient;

    /**
     * JSON - dane sprzedającego wyświetlane na ofercie.
     */
    seller?: SalesDocumentClient;

    /**
     * Możliwość edycji zamówienia przez: tylko sprzedawcę lub także kupującego. null - brak ograniczeń
     */
    allowedEditBy?: 'sales' | 'all' | null;

    /**
     * Id wersji cenowej
     */
    priceVersionId?: number;

    /**
     * Termin ważności oferty bazujący na długości ważności oferty w dniach
     * Nie jest obowiązujący
     * Aby wyliczyć faktyczny obowiązujący termin oferty,
     * bazujący na tym polu i terminie ważności wersji cenowej
     * należy użyć funkcji
     * calculateValidity() z OfferValidityService
     */
    validityDate?:Date;

    /**
     * Pozwola na zignorowanie terminu ważności wersji cenowej (expiredDate).
     */
    ignorePriceVersionValidity?: boolean;

    /**
     * Termin realizacji oczekiwany przez kupującego
     */
    requestedDeadline?: Date;

    /**
     * Termin dostawy (faktycznie, a nie deklarowany)
     */
    deliveryDate?: Date;

    /**
     * Termin realizacji w dniach
     */
    timeLimitDays?: number;

    /**
     * Czy została opłacona zaliczka
     */
    isAdvancePaid?: boolean;

    /**
     * Czy dokument został w całości opłacony
     */
    isTotalPaid?: boolean;

    /**
     * Czy zamówienie zostało dostarczone
     */
    isDelivered?: boolean;

    /**
     * Czy dokument został anulowany?
     */
    isCanceled?: boolean;

    /**
     * Czy dokument został potwierdzony?
     */
    isConfirmed?: boolean;

    /**
     * Czy dokument został zamówiony?
     */
    isOrderPlaced?: boolean;

    /**
     * Czy dokument został przygotowany do zamówienia?
     */
    isOrderCreated?: boolean;

    /**
     * Czy dokument został przesłany do klienta
     */
    isSentToBuyer?:boolean;

    /**
     * Czy dokument został przesłany do klienta i został utworzony jego dokument sprzedaży.
     */
    isCreatedBuyerDocument?: boolean;

    /**
     * Czy dokument został otwarty, przyjęty?
     */
    isOpened?:boolean;

    /**
     * Czy dokument został przyjęty do realizacji?
     */
    isAcceptedForProcessing?:boolean;

    /**
     * Czy dokument jest w trakcie realizacji?
     */
    isInProgress?:boolean;

    /**
     * Czy dokument został zrealizowany?
     */
    isCompleted?:boolean;

    /**
     * Czy zamówienie zostało aneksowane
     */
    isAnnexed?: boolean;

    /**
     * Czy zamówienie jest otwarte do edycji tylko w zakresie pozycji własnych (w zakresie sprzedaży)?
     */
    isConfirmedOnlySalesPositions?: boolean;

    /**
     * Domyślna stawki VAT
     */
    defaultTaxRate?: number;

    /**
     * ID domyślnej stawki VAT
     */
    defaultTaxRateId?: number;

    /**
     * Czy włączony tryb modyfikacji stawek VAT?
     */
    isCustomTaxRatesMode?: boolean;

    /**
     * Czy jedna stawka podatku VAT na całym dokumencie?
     */
    isOneTaxRateMode?: boolean;

    /**
     * Metoda kalkulacji podatku VAT:
     * * 'netSummary' - od netto, VAT w podsumowaniu
     * * 'netPosition' - od netto, VAT do wartości pozycji
     * * 'grossSummary' - od brutto, VAT w podsumowaniu
     */
    taxRateCalculationMethod?: 'netSummary' | 'netPosition' | 'grossSummary';

    /**
     * Marża w procentach
     */
    priceMarginPercent?: number;

    /**
     * Waluta w postaci kodu zgodnego ze standardem ISO 4217
     */
    priceCurrency?: string;

    /**
     * Waluta w postaci kodu zgodnego ze standardem ISO 4217
     */
    exchangeRate?: SalesDocumentExchangeRate;

    /**
     * Czy kwota jest brutto?
     */
    priceIsGross?: boolean;

    /**
     * Wartość przed promocją i rabatami
     */
    priceBeforePromotionsValue?: number;

    /**
     * Promocje w postaci JSONa
     */
    pricePromotions?: any; //Promotions;

    /**
     * Wartość przed rabatami
     */
    priceBeforeDiscountsValue?: number;

    /**
     * Rabaty w postaci JSONa
     */
    priceDiscounts?: any; //Discounts;

    /**
     * Rabat dodatkowy
     */
    priceAdditionalDiscount?: number;

    /**
     * Wartość po rabatach
     */
    priceAfterDiscountsValue?: number;

    /**
     * Wartość po rabatach
     */
    discountType: 'price' | 'value';

    /**
     * Czy dokument posiada niewycenione pozycje
     */
    hasNonValuatedPositions?: boolean;

    /**
     * Czy dokument posiada wyłącznie ręcznie wyceniane pozycje
     */
    hasOnlyCustomValuatedPositions?: boolean;

    /**
     * Czas złożenia zamówienia
     */
    orderPlacedAt?: Date | null;

    /**
     * Jednostki miary, w których została sporządzona oferta
     */
    dimensionUnits?: 'imperial' | 'international';

    /**
     * Opis dokumentu - komentarz ustawiany przez dystrybutora
     */
    description?: string;

    /** Uwagi dla producenta */
    producerNotes?: string;

    /** Id użytkownika zatwierdzającego ofertę */
    confirmerUserId?: number;

    /** Id użytkownika składającego zamówienie do zatwierdzenia */
    unplacedOrderUserId?: number;

    email?: null | {
        uuid: string;
        type: 'order' | 'valuation' | 'toPlaceOrder' | 'consult' | 'confirmOrderChanges';
        documentIdToSendEmail: SalesDocument['uuid'];
        neededDocumentsIds: Record<SalesDocument['uuid'], boolean>;
    };
}


export type SalesDocumentDTO = Omit<SalesDocument, 'externalIds' | 'documentNumber' | 'pricePromotions' | 'priceDiscounts' | 'client' | 'seller' | 'groups' | 'exchangeRate' | 'flags' | 'email' | '$dirty' | 'createdServer' | 'author'> & {
    documentNumber?: string;
    externalIds?: string;
    pricePromotions?: string;
    priceDiscounts?: string;
    exchangeRate?: string;
    client?: string;
    seller?: string;
    groups?: string;
    flags?: string;
    email?: string;
    author?: string;
};

export const SalesDocumentNonReadableProps = [
    'id',
    'uuid',
    'externalSource',
    'partialExternalSource',
    'authorCompanyId',
    'authorUserId',
    'author',
    'type',
    'salesDocumentTypeId',
    'statusSystemId',
    'rootId',
    'parentId',
    'version',
    'searchableDocumentNumber',
    'number',
    'documentCustomCode',
    'validityDate',
    'ignorePriceVersionValidity',
    'groups',
    'defaultTaxRate',
    'defaultTaxRateId',
    'pricePromotions',
    'priceDiscounts',
    'priceBeforePromotionsValue',
    'priceBeforeDiscountsValue',
    'hasNonValuatedPositions',
    'hasOnlyCustomValuatedPositions',
    'created',
    'modified',
    'createdServer',
    'modifiedServer',
    'deletedAt',
    'rootCreated',
    'machineId',
    '$dirty',
    'toSendToExternalSystem',
    'unplacedOrderUserId',
    'confirmerUserId',
    'email',
    'exchangeRate',
    'isCreatedBuyerDocument',
    'orderPlacedAt',
] as const satisfies readonly (keyof SalesDocument)[];

export const SalesDocumentReadablePropsNames: Record<Exclude<keyof SalesDocument, typeof SalesDocumentNonReadableProps[number]>, string> = {
    seller: _('SALES_DOCUMENT|Sprzedawca'),
    documentNumber: _('SALES_DOCUMENT|Numer dokumentu'),
    client: _('SALES_DOCUMENT|Klient'),
    flags: _('SALES_DOCUMENT|Flagi'),
    externalId: _('SALES_DOCUMENT|Zewnętrzny ID'),
    externalIds: _('SALES_DOCUMENT|Zewnętrzne ID - dodatkowe'),
    externalChanceId: _('SALES_DOCUMENT|ID szansy sprzedażowej'),
    sellerMarketId: _('SALES_DOCUMENT|Rynek sprzedający'),
    sellerCompanyId: _('SALES_DOCUMENT|Firma sprzedający'),
    sellerResourceId: _('SALES_DOCUMENT|Oddział sprzedający'),
    sellerUserId: _('SALES_DOCUMENT|Użytkownik sprzedający'),
    sellerProductDepartmentId: _('SALES_DOCUMENT|Dział produktowy sprzedający'),
    buyerCompanyId: _('SALES_DOCUMENT|Firma kupujący'),
    buyerResourceId: _('SALES_DOCUMENT|Oddział kupujący'),
    buyerUserId: _('SALES_DOCUMENT|Użytkownik kupujący'),
    buyerContractorId: _('SALES_DOCUMENT|Kontrahent kupujący'),
    documentName: _('SALES_DOCUMENT|Nazwa dokumentu'),
    statusId: _('SALES_DOCUMENT|Status dokumentu'),
    assignedTo: _('SALES_DOCUMENT|Przypisany do'),
    clientReference: _('SALES_DOCUMENT|Referencja klienta'),
    declaredDeadline: _('SALES_DOCUMENT|Termin realizacji deklarowany'),
    priceVersionId: _('PRICE_VERSION|Wersja cenowa'),
    requestedDeadline: _('SALES_DOCUMENT|Termin realizacji oczekiwany'),
    deliveryDate: _('SALES_DOCUMENT|Termin dostawy'),
    timeLimitDays: _('SALES_DOCUMENT|Termin realizacji w dniach'),
    isAdvancePaid: _('SALES_DOCUMENT|Zaliczka opłacona'),
    isTotalPaid: _('SALES_DOCUMENT|Opłacony w całości'),
    isDelivered: _('SALES_DOCUMENT|Dostarczony'),
    isDeleted: _('SALES_DOCUMENT|Usunięty'),
    isCanceled: _('SALES_DOCUMENT|Anulowany'),
    isConfirmed: _('SALES_DOCUMENT|Potwierdzony'),
    isOrderPlaced: _('SALES_DOCUMENT|Zamówiony'),
    isOrderCreated: _('SALES_DOCUMENT|Przygotowany do zamówienia'),
    isSentToBuyer: _('SALES_DOCUMENT|Wysłany do klienta'),
    isOpened: _('SALES_DOCUMENT|Otwarty'),
    isAcceptedForProcessing: _('SALES_DOCUMENT|Przyjęty do realizacji'),
    isInProgress: _('SALES_DOCUMENT|W trakcie realizacji'),
    isCompleted: _('SALES_DOCUMENT|Zrealizowany'),
    isAnnexed: _('SALES_DOCUMENT|Aneksowany'),
    isConfirmedOnlySalesPositions: _('SALES_DOCUMENT|Edycja tylko pozycji sprzedaży'),
    isEmpty: _('SALES_DOCUMENT|Bez pozycji'),
    isInvalid: _('SALES_DOCUMENT|Niepoprawny'),
    isCustomTaxRatesMode: _('SALES_DOCUMENT|Włączony tryb modyfikacji stawek VAT'),
    isOneTaxRateMode: _('SALES_DOCUMENT|Jedna stawka podatku VAT na całym dokumencie'),
    taxRateCalculationMethod: _('SALES_DOCUMENT|Metoda kalkulacji podatku VAT'),
    priceMarginPercent: _('SALES_DOCUMENT|Marża w procentach'),
    priceCurrency: _('SALES_DOCUMENT|Waluta'),
    priceIsGross: _('SALES_DOCUMENT|Czy brutto'),
    priceAdditionalDiscount: _('SALES_DOCUMENT|Rabat dodatkowy'),
    priceAfterDiscountsValue: _('SALES_DOCUMENT|Wartość po rabatach'),
    discountType: _('SALES_DOCUMENT|Typ rabatu'),
    dimensionUnits: _('SALES_DOCUMENT|Jednostki miary'),
    lang: _('SALES_DOCUMENT|Język'),
    description: _('SALES_DOCUMENT|Opis'),
    producerNotes: _('SALES_DOCUMENT|Uwagi dla producenta'),
    documentCode: _('SALES_DOCUMENT|Własny kod oferty / zamówienia'),
    allowedEditBy: _('SALES_DOCUMENT|Możliwość edycji przez'),
};